import React, { useState, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "components/button";
import WeightInput from "components/weight-input";
import { getQueryParams } from "lib/utils";
import { useTranslation } from "react-i18next";
import AuthContext from "contexts/AuthContext";
import { useQuery } from "react-query";
import { getFreshAccessToken } from "../../api/use-user";

export default function ConfirmWeight() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, authTokens } = useContext(AuthContext);
  const queryParams = getQueryParams(location.search);
  const [weight, setWeight] = useState(queryParams.weight || "");

  // Check if this is for in-store shipping (has correlationId and eventId)
  const isInStoreShipping = !!(
    queryParams.correlationId && queryParams.eventId
  );

  // Query shipping data if this is in-store shipping
  const { data: shippingData, isLoading } = useQuery(
    ["inStoreShipping", queryParams.correlationId],
    async () => {
      if (!isInStoreShipping) return null;

      const accessToken = getFreshAccessToken();

      // Poll the in-store shipping API endpoint to get shipping data
      const response = await fetch(
        `/api/shipping/status?correlationId=${queryParams.correlationId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch shipping status");
      }

      const result = await response.json();
      return result.data?.[0]; // Return first shipping item
    },
    {
      enabled: isInStoreShipping,
      refetchInterval: 3000, // Poll every 3s until we get data
    }
  );

  // Extract shipping label data
  const originalLabelUrl = shippingData?.shipping_metadatas?.items?.find(
    (item) => item.key === "label_url"
  )?.value;

  // Construct label URL similar to the working example
  const labelUrl = useMemo(() => {
    if (originalLabelUrl && queryParams.correlationId) {
      try {
        const url = new URL(originalLabelUrl);
        const returnID = url.searchParams.get("returnID");

        if (returnID && authTokens?.access_token) {
          return `${window.location.origin}/api/pdf/shipping-label-no-instructions?returnID=${returnID}&token=${authTokens.access_token}`;
        }
        return originalLabelUrl;
      } catch (error) {
        console.error("Error constructing label URL:", error);
        return originalLabelUrl;
      }
    }
    return originalLabelUrl;
  }, [originalLabelUrl, queryParams.correlationId, authTokens.access_token]);

  const handleWeightChange = (e) => {
    setWeight(e.target.value);
  };

  const handlePrintShippingLabel = () => {
    if (labelUrl) {
      // Open PDF in new tab for printing
      window.open(labelUrl, "_blank");
    }
  };

  const handleGenerateShippingLabel = () => {
    if (isInStoreShipping) {
      handlePrintShippingLabel();
      return;
    }

    // Original flow for regular shipping
    const timestamp = new Date().toISOString();
    const email = user?.email;
    const storeId = email.includes("testinstoreGA")
      ? "sugarloaf"
      : email.includes("testinstoreCO")
        ? `in_store_co`
        : undefined;

    const searchParams = new URLSearchParams({
      weight,
      email,
      timestamp,
      storeId,
    }).toString();

    navigate(`${t("urls.store_package_qr_code")}?${searchParams}`);
  };

  // Determine button text and loading state
  const getButtonText = () => {
    if (isInStoreShipping) {
      if (isLoading) return "Processing...";
      if (labelUrl) return "Print Shipping Label";
      return "Waiting for Label...";
    }
    return "Generate Shipping Label";
  };

  const getDescription = () => {
    if (isInStoreShipping) {
      if (isLoading) return "Processing your shipping label request...";
      if (labelUrl) return "Your shipping label is ready to print.";
      return "Please wait while we generate your shipping label.";
    }
    return "Please confirm the package weight before generating the shipping label.";
  };

  return (
    <div className="flex flex-col h-[calc(100vh-81px)]">
      <div className="flex flex-grow flex-col items-center justify-center px-6 py-4">
        <h1 className="text-6xl text-primary mb-regular">
          {isInStoreShipping && labelUrl
            ? "Shipping Label Ready"
            : "Confirm Weight"}
        </h1>
        <p className="text-center sm:max-w-md text-2xl mb-x-large text-secondary">
          {getDescription()}
        </p>

        {isInStoreShipping && queryParams.correlationId && (
          <div className="bg-gray-100 p-4 rounded-lg mb-6">
            <p className="text-lg font-semibold">
              Correlation ID: {queryParams.correlationId}
            </p>
            {queryParams.eventId && (
              <p className="text-sm text-gray-600">
                Event ID: {queryParams.eventId}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col gap-4 mt-10 ">
          <WeightInput
            caption="Estimated Package Weight"
            unit={`${t("scale.weight")}`}
            value={weight}
            onChange={handleWeightChange}
          />
          <Button
            onClick={handleGenerateShippingLabel}
            className="w-full max-w-sm py-6 mb-2 mt-4"
            disabled={!weight || (isInStoreShipping && !labelUrl && !isLoading)}
          >
            {getButtonText()}
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate(-1)}
            className="w-full max-w-sm py-6 mb-2 mt-4"
          >
            Back
          </Button>
        </div>
      </div>
    </div>
  );
}
